import { Add, Info } from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent, { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { addDays, differenceInCalendarDays, isSameDay, set } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { EditButtonGroup } from '../../../components/general';
import { type Veranstaltung, type Zeitraum } from '../../../dtos';
import { trpc } from '../../../trpc';
import { addDaysToZeitraum } from '../../../utils/zeitraum.js';
import { dayNumber } from './ablaufUtils.js';
import { content } from './TerminAblaufCard.content';
import { TimeRangeEdit } from './TimeRangeEdit';

type VeranstaltungPick = Pick<
  Veranstaltung,
  'akaVeranstaltungId' | 'ablauf' | 'experteBlockungen' | 'raumBlockungen' | 'standortBlockungen'
>;

type TerminAblaufCardProps = {
  readonly veranstaltung: VeranstaltungPick;
  readonly endEdit: () => void;
  readonly isTerminreihe: boolean;
};

const defaultZeitraum = {
  start: set(new Date(), { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 }),
  end: set(new Date(), { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }),
};

const hasBlockungen = (
  veranstaltung: Pick<Veranstaltung, 'experteBlockungen' | 'raumBlockungen' | 'standortBlockungen'>,
): boolean =>
  veranstaltung.raumBlockungen.length > 0 ||
  veranstaltung.experteBlockungen.length > 0 ||
  veranstaltung.standortBlockungen.length > 0;

export const TerminAblaufEditCard = ({ veranstaltung, endEdit, isTerminreihe }: TerminAblaufCardProps) => {
  const { akaVeranstaltungId, ablauf: originalAblauf } = veranstaltung;

  const [ablauf, setAblauf] = useState(originalAblauf);
  const firstDay = ablauf[0].start;

  const { enqueueSnackbar } = useSnackbar();

  const ablaufMutation = trpc.veranstaltung.updateAblauf.useMutation({
    onSuccess: () => {
      enqueueSnackbar(content.ABLAUF_GEAENDERT, { variant: 'success' });
    },
    onError(error) {
      enqueueSnackbar(content.errorMessages.updateFailed(error.message), { variant: 'error', persist: true });
      setAblauf(originalAblauf);
    },
  });

  const addAblauf = (): void =>
    setAblauf((prev) => {
      const currentLastDay = prev.at(-1) ?? defaultZeitraum;
      const newStart = addDays(currentLastDay.start, 1);
      const newEnd = addDays(currentLastDay.end, 1);
      return [...prev, { start: newStart, end: newEnd }];
    });

  const removeDay = (index: number): void => {
    setAblauf((prevAblauf) => prevAblauf.filter((_, i) => i !== index));
  };

  const onEditCancel = (): void => {
    setAblauf(originalAblauf);
  };

  const onSave = async (): Promise<void> => {
    await ablaufMutation.mutateAsync({ akaVeranstaltungId, ablauf });
  };

  const ablaufTage = ablauf.map((item) => item.start);

  const onDateTimeChange = (indexTimeChange: number, start: Date | null, end: Date | null): void => {
    setAblauf((prev) => {
      const newAblaufArray = [...prev];
      newAblaufArray[indexTimeChange] = {
        start: start ?? prev[indexTimeChange].start,
        end: end ?? prev[indexTimeChange].end,
      };
      newAblaufArray.sort((a, b) => a.start.getTime() - b.start.getTime());

      return newAblaufArray;
    });
  };

  const shiftDates = (newDate: Date | null): void => {
    if (!newDate) {
      return;
    }

    const diff = differenceInCalendarDays(newDate, ablauf[0].start);
    setAblauf((previousAblauf: Zeitraum[]) => previousAblauf.map((item) => addDaysToZeitraum(item, diff)));
  };

  const blockungenExist = hasBlockungen(veranstaltung);

  const isFirstOrLastDay = (index: number): boolean => index === 0 || index === ablauf.length - 1;

  const showRemoveButton = (index: number): boolean => {
    if (ablauf.length <= 1) {
      return false;
    }

    if (isTerminreihe) {
      return true;
    }

    return isFirstOrLastDay(index);
  };

  return (
    <Stack>
      <Box display="flex" justifyContent="center" height="100%" alignItems="center">
        <DatePicker
          disabled={blockungenExist}
          value={ablauf[0].start}
          label="Start-Datum verschieben"
          onChange={shiftDates}
          slotProps={{
            textField: {
              inputProps: { 'data-testid': 'shift-date-picker' },
            },
          }}
        />
        {blockungenExist && (
          <Tooltip title={content.BLOCKUNGEN_VORHANDEN_TOOLTIP} sx={{ ml: 1 }}>
            <Info color="primary" />
          </Tooltip>
        )}
        <EditButtonGroup isEditMode setEditMode={endEdit} onSave={onSave} onCancel={onEditCancel} />
      </Box>
      <Timeline
        sx={{
          pl: 0,
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.1,
            pl: 0,
          },
        }}
      >
        {ablauf.map((zeitraum, idx) => (
          <TimelineItem key={zeitraum.start.getTime()}>
            <TimelineOppositeContent align="right" sx={{ whiteSpace: 'nowrap' }}>
              {dayNumber(firstDay, zeitraum.start)}. Tag
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              {idx + 1 < ablauf.length ? <TimelineConnector /> : null}
            </TimelineSeparator>
            <TimelineContent>
              <TimeRangeEdit
                idx={idx}
                start={zeitraum.start}
                end={zeitraum.end}
                onChange={onDateTimeChange}
                onRemove={removeDay}
                unavailableDates={ablaufTage.filter((item) => !isSameDay(item, zeitraum.start))}
                showRemoveButton={showRemoveButton(idx)}
                disableDateEdit={blockungenExist}
              />
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button data-testid="add-day-button" onClick={addAblauf} startIcon={<Add />} size="small">
          {content.TAG_HINZUFUEGEN}
        </Button>
      </Stack>
    </Stack>
  );
};
